import Vue from 'vue';
import * as Sentry from '@sentry/vue';
import { Integrations } from '@sentry/tracing';
import LogRocket from 'logrocket';
import Vuex from 'vuex';
// import Vuetify from 'vuetify';
import PulseLoader from 'vue-spinner/src/PulseLoader.vue';
import ClipLoader from 'vue-spinner/src/ClipLoader.vue';
import JsonCSV from 'vue-json-csv';
// import sanitizeHTML from 'sanitize-html';
import VueDOMPurifyHTML from 'vue-dompurify-html';
import GmapVue from 'gmap-vue';
import VueAnalytics from 'vue-analytics';
import DatetimePicker from 'vuetify-datetime-picker';
import { VueMaskDirective } from 'v-mask';
import VueSocialSharing from 'vue-social-sharing';
import VueTour from 'vue-tour';
import VueApexCharts from 'vue-apexcharts';
import store from './store';
import router from './router';
import App from './App';
import vuetify from './plugins/vuetify';
import 'vuetify/dist/vuetify.min.css';
import 'vue-tour/dist/vue-tour.css';
import '@/assets/css/variables.css';
import '@/assets/css/transitions.css';
import Configuration from './utils/configuration';
import { firebaseApp } from './utils/firebaseConfig';
import i18n from './i18n';
import '@stripe/stripe-js'; //* import globally to enable Stripe fraud protection
import e6Package from '../package';
import { baseURL, NODE_ENV, VUE_APP_NAME, VUE_APP_VERSION } from '@/config/appSettings.js';
// import * as filters from './utils/filters';

const GOOGLE_MAPS_KEY = Configuration.value('VUE_APP_GOOGLE_MAPS_KEY');
const SITE_ENV = Configuration.value('VUE_APP_SITE_ENV');
const analyticsID =
  SITE_ENV === 'staging'
    ? 'UA-17371661-6'
    : SITE_ENV === 'production'
    ? 'UA-17371661-5'
    : 'UA-17371661-6';

if (NODE_ENV !== 'development') {
  Sentry.init({
    Vue,
    dsn: 'https://2ca7337c025241a2ab9c713d3390d507@o102399.ingest.sentry.io/248719',
    release: `${VUE_APP_NAME}@${VUE_APP_VERSION}`,
    integrations: [
      new Integrations.BrowserTracing({
        tracingOrigins: [
          'localhost',
          // 'qa.e6golf.com',
          // 'portal.e6golf.com',
          // 'api.e6golf.io',
          // 'api.e6golf.com',
          // /^\//,
        ],
        routingInstrumentation: Sentry.vueRouterInstrumentation(router),
      }),
    ],
    debug: NODE_ENV !== 'production',
    tracesSampleRate: NODE_ENV === 'production' ? 0.2 : 1,
    tracingOptions: {
      trackComponents: true,
    },
    // Vue specific
    logErrors: NODE_ENV !== 'production',
    attachProps: true,
    attachStacktrace: true,
    ignoreErrors: [
      // Custom
      'Non-Error exception captured',
      'Non-Error promise rejection captured',
      'ChunkLoadError',
      'Loading chunk',
      'Loading CSS chunk',
      `'Can't find variable: instantSearchSDKJSBridgeClearHighlight'`,
      'InvalidValueError: Map: Expected mapDiv of type HTMLElement but was passed undefined.',
      `null is not an object (evaluating 'A[0]')`,
      `Cannot read properties of null (reading '0')`,
      `null is not an object (evaluating 'this.node()[t]')`,
      // Random plugins/extensions
      'top.GLOBALS',
      // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      'http://tt.epicplay.com',
      `'Can't find variable: ZiteReader'`,
      'jigsaw is not defined',
      'ComboSearch is not defined',
      'http://loading.retry.widdit.com/',
      'atomicFindClose',
      // Facebook borked
      'fb_xd_fragment',
      // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to
      // reduce this. (thanks @acdha)
      // See http://stackoverflow.com/questions/4113268
      'bmi_SafeAddOnload',
      'EBCallBackMessageReceived',
      // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
      'conduitPage',
    ],
    ignoreUrls: [
      // Facebook flakiness
      /graph\.facebook\.com/i,
      // Facebook blocked
      /connect\.facebook\.net\/en_US\/all\.js/i,
      // Woopra flakiness
      /eatdifferent\.com\.woopra-ns\.com/i,
      /static\.woopra\.com\/js\/woopra\.js/i,
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      // Other plugins
      /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
      /webappstoolbarba\.texthelp\.com\//i,
      /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
      /portal-e6golf-com\.translate\.goog/,
    ],
    //* Disable until I figure out why it's causing so many errors
    beforeSend(event, hint) {
      /* eslint:disable:no-string-literal only-arrow-functions */
      const error = hint?.originalException;
      //* We want to ignore those kind of errors
      if (
        (error && error?.message && error?.message?.startsWith('Non-Error exception captured')) ||
        error?.message?.startsWith('Non-Error promise rejection') ||
        error?.message?.startsWith('Loading CSS chunk')
      ) {
        return null;
      }
      if (
        error &&
        error?.message &&
        (error?.message?.startsWith(`null is not an object (evaluating 'A[0]')`) ||
          error?.message?.startsWith(`Cannot read properties of null (reading '0')`))
      ) {
        return null;
      }
      return event;
    },
  });

  //* Configure VueAnalytics
  Vue.use(VueAnalytics, {
    id: `${analyticsID}`,
    router,
  });

  if (SITE_ENV !== 'staging' && SITE_ENV !== 'qa') {
    //* LogRocket Setup
    LogRocket.init('b6ydwu/tg-web', {
      release: e6Package.version,
    });
    LogRocket.getSessionURL((sessionURL) => {
      Sentry.configureScope((scope) => {
        scope.setExtra('sessionURL', sessionURL);
      });
    });
  }
}

// //* register global utility filters.
// Object.keys(filters).forEach((key) => {
//   Vue.filter(key, filters[key]);
// });

// Vue.use(Vuetify);
Vue.config.productionTip = false;
Vue.use(Vuex);
Vue.use(GmapVue, {
  load: {
    key: GOOGLE_MAPS_KEY,
    installComponents: true,
  },
});
Vue.use(VueSocialSharing);
// TODO only import these on pages using it
Vue.use(DatetimePicker);
Vue.use(VueDOMPurifyHTML);
// Vue.prototype.$sanitize = sanitizeHTML;
Vue.use(VueTour);
Vue.use(VueApexCharts);

//* Register global components
Vue.component('pulse-loader', PulseLoader);
Vue.component('clip-loader', ClipLoader);
Vue.component('downloadCsv', JsonCSV); // TODO only import this on pages using it
Vue.directive('mask', VueMaskDirective);
Vue.component('apexchart', VueApexCharts); //* look into only importing in single file

/* eslint-disable no-new */
new Vue({
  router,
  store,
  vuetify,
  i18n,
  render: (h) => {
    return h(App);
  },
  created() {
    //* dispatch an action on instantiation
    // this.$store.dispatch('users/setClub')
    //* initialize Firebase
    firebaseApp();
  },
}).$mount('#app');
